/**
 * Member Suite — Experience · Itinerary (Wave 26.21 + Phase 2 · Pass 2 · Slice B)
 *
 * Routing wrapper. If the trip has a PUBLISHED design_studio itinerary
 * (`trip_itineraries` doc with status=published), render the new
 * `ClientItineraryView`. Otherwise, fall back to the legacy `ClientItinerary`
 * so Jackson + unpublished design_studio trips keep working unchanged.
 */
import React from "react";
import { useParams } from "react-router-dom";
import ClientItinerary from "../Itinerary";
import ClientItineraryView from "./ItineraryView";
import { api } from "../../../index";

export default function ExperienceItinerary() {
  const { tripId } = useParams();
  const [mode, setMode] = React.useState("loading"); // loading | new | legacy

  React.useEffect(() => {
    let alive = true;
    if (!tripId) { setMode("legacy"); return undefined; }
    const isAdvisorPreview =
      typeof window !== "undefined" && (window.location.pathname || "").includes("/portal/advisor/");
    const path = isAdvisorPreview
      ? `/design-studio/${tripId}/itinerary`
      : `/client-portal/trips/${tripId}/design-studio-itinerary`;
    api
      .get(path)
      .then(({ data }) => {
        if (!alive) return;
        // Route to the new view when the trip has any design_studio
        // itinerary footprint: published, drafted days, or a headline.
        // Otherwise (e.g. concierge trips that auto-seed an empty doc)
        // fall back to the legacy ClientItinerary.
        const hasFootprint =
          data?.status === "published" ||
          (Array.isArray(data?.days) && data.days.length > 0) ||
          Boolean(data?.headline);
        setMode(hasFootprint ? "new" : "legacy");
      })
      .catch((err) => {
        if (!alive) return;
        // 404 from the client-portal endpoint means "no published design_studio
        // itinerary" — fall back to legacy (concierge trips, unpublished trips).
        setMode("legacy");
        // suppress unused var warning
        void err;
      });
    return () => { alive = false; };
  }, [tripId]);

  if (mode === "loading") {
    return (
      <p
        className="py-20 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]"
        data-testid="experience-itinerary-loading"
      >
        Loading your itinerary…
      </p>
    );
  }
  if (mode === "new") return <ClientItineraryView tripId={tripId} />;
  return <ClientItinerary />;
}
